import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import { Button, Modal, Form } from "react-bootstrap";
import editIcon from "../../assets/img/edit-icon.png";
import crossIcon from "../../assets/img/cross-icon.png";
import { useDispatch, useSelector } from "react-redux";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useForm } from "react-hook-form";
import { createHRAssignment, fetchHRAssignment } from "../../redux/slice/supportConfigSlice";
import useCredentialMasterData from "hooks/useCredentialData";
import { format } from "date-fns";
import { fetchAllCategories } from "../../redux/slice/supportSlice";

function AssignmentCategory() {
    const dispatch = useDispatch();
    const { register, handleSubmit, reset, formState: { errors } } =
        useForm();
    const { activeEmp } = useCredentialMasterData(true);
    const [configUpdateId, setConfigUpdateId] = useState(null);
    const [showConfigUpdateModal, setShowConfigUpdateModal] = useState(false);
    const [editConfig, setEditConfig] = useState({
        name: "",
        value: "",
        type: "",
        desc: "",
    });
    const { categories } = useSelector(
        (state) => state.support
    );
    const { assignment, assignmentStatus } = useSelector(
        (state) => state.supportConfig
    );
    const canView = useHasPermission("Settings-view");
    const canEdit = useHasPermission("Settings-manage");

    useEffect(() => {
        if (canView) {
            dispatch(fetchHRAssignment());
            dispatch(fetchAllCategories());
        }
    }, [dispatch, canView]);

    const assigneeOptions = activeEmp
        ? activeEmp
            .filter((item) => item.roleName !== "Employee")
            .map((item) => ({
                value: item.userId,
                label: `${item.name} - ${item.roleName}`,
            }))
        : [];

    if (!canView) {
        return (
            <p className="perm-msg">
                You don’t have permission to view settings page!
            </p>
        );
    }

    const handleEditConfig = (config) => {
        console.log("config:", config);
        setConfigUpdateId(config.id);
        setEditConfig({
            categoryName: config.categoryName,
            assignedToUserId: String(config.assignedToUserId),
        });
        setShowConfigUpdateModal(true);
    };

    const handleUpdateConfig = async () => {
        if (!canEdit) {
            toast.error("You don't have permission to edit configuration", {
                className: "toast-error-custom",
                autoClose: 5000,
            });
            return;
        }
        if (configUpdateId === null) return;
        try {
            await dispatch(
                createHRAssignment({
                        categoryName: editConfig.categoryName,
                        assignedToUserId: editConfig.assignedToUserId,
                })
            ).unwrap();
            toast.success("Configuration updated successfully!", {
                className: "toast-success-custom",
            });
        } catch (error) {
            toast.error(error?.message || "Failed to update configuration!");
        } finally {
            // always cleanup modal, ids, and trigger a refresh regardless of success or failure
            setShowConfigUpdateModal(false);
            setConfigUpdateId(null);
            dispatch(fetchHRAssignment()); 
        }
    };

    const handleFormSubmit = async (data) => {
        if (!canEdit) {
            toast.error("You don't have permission to save configuration", {
                className: "toast-error-custom",
                autoClose: 5000,
            });
            return;
        }

        const payload = {
            categoryName: data.category,
            assignedToUserId: data.assignedTo,
        };
        try {
            await dispatch(createHRAssignment(payload)).unwrap();
            toast.success("Configuration has been created sucessfully!", {
                className: "toast-success-custom",
            });
        } catch (err) {
            toast.error(err || "unable to connect to server!", {
                className: "toast-error-custom",
            });
        }finally {
            dispatch(fetchHRAssignment());
            reset();
        }
    };

    return (
        <div>
            <div className="section-body m-0">
                <div className="container-fluid">
                    <div className="settings-heading pt-3 pt-md-0">
                        <h5>ASSIGNMENT FOR CATEGORY</h5>
                        <p>Define the primary responders for each support ticket category, ensuring requests are automatically routed to the correct designated user for faster resolution.</p>
                    </div>
                    <div className="row clearfix">
                        <div className="col-lg-12 col-md-12">
                            <div className="setting-container">
                                <ul className="list-group mb-3">
                                    <li className="list-group-item">
                                        <div className="media mb-0">
                                            <div className="config-form p-0">
                                                <form onSubmit={handleSubmit(handleFormSubmit)}>
                                                    <div>
                                                        <div className="input-text">
                                                            <p>
                                                                Support Category{" "}
                                                                <span style={{ color: "red" }}>*</span>
                                                            </p>
                                                            <div className="user-input-icons">
                                                                <select
                                                                    className="input-field form-select-custom custom-dropdown"
                                                                    disabled={!canEdit}
                                                                    {...register("category", {
                                                                        required: "Please select the support category",
                                                                    })}
                                                                >
                                                                    <option value="" hidden>
                                                                        Please select the support category
                                                                    </option>

                                                                    {categories.map((cat) => {
                                                                        const rawName = cat.name || "";
                                                                        const displayName = rawName
                                                                            .replace(/[_-]/g, " ")
                                                                            .toLowerCase();

                                                                        return (
                                                                            <option key={cat.id} value={rawName}>
                                                                                {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                                {errors.category && (
                                                                    <div className="userErrorMessage">
                                                                        {errors.category.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="input-text">
                                                            <p>
                                                                Assigned To{" "}
                                                                <span style={{ color: "red" }}>*</span>
                                                            </p>
                                                            <div className="user-input-icons">
                                                                <select
                                                                    className="input-field form-select-custom custom-dropdown"
                                                                    disabled={!canEdit}
                                                                    {...register("assignedTo", {
                                                                        required: "Please select the assignee",
                                                                    })}
                                                                >
                                                                    <option value="" hidden>
                                                                        Please select the assignee
                                                                    </option>

                                                                    {assigneeOptions.map(assigned => {
                                                                        return (<option key={assigned.value} value={assigned.value}>
                                                                            {assigned.label?.replace(/_/g, " ")}
                                                                        </option>)
                                                                    })
                                                                    }
                                                                </select>
                                                                {errors.assignedTo && (
                                                                    <div className="userErrorMessage">
                                                                        {errors.assignedTo.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-center mt-4">
                                                            <button
                                                                className="primary-btn"
                                                                type="submit"
                                                                title={
                                                                    canEdit
                                                                        ? "save configuration"
                                                                        : "No permission to save configuration"
                                                                }
                                                                disabled={!canEdit}
                                                            >
                                                                Save Configuration
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                            <ToastContainer />
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-12 col-md-12">
                            <div className="card">
                                <div className="card-header">
                                    <p className="card-title">
                                        <b>Configuration List</b>
                                    </p>
                                </div>
                                <div className="card-body">
                                    <div className="row clearfix">
                                        <div className="role-data table-responsive mt-0">
                                            {assignmentStatus === "loading" && (
                                                <p className="loading">Loading...</p>
                                            )}

                                            {assignmentStatus === "Failed" && (
                                                <p className="server-error-msg">Server Error...</p>
                                            )}
                                            {assignmentStatus == "succeeded" &&
                                                (assignment.data.length > 0 ? (
                                                    <table className="role-data-table table table-hover table-vcenter text-nowrap mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th className="table-row">Name</th>
                                                                <th className="table-row">Assigned To</th>
                                                                <th className="table-row">Created At</th>
                                                                <th className="table-row">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {assignment.data.map((config) => (
                                                                <tr key={config.id}>
                                                                    <td>
                                                                        {config.categoryName?.replace(/_/g, " ")
                                                                            .toLowerCase()
                                                                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                                                                    </td>
                                                                    <td>
                                                                        {config.assignedToUserName}
                                                                    </td>
                                                                    <td>
                                                                        {config.createdAt ? format(new Date(config.createdAt), "M/d/yyyy, h:mm a") : "---"}
                                                                    </td>
                                                                    <td className="action-column">
                                                                        <button
                                                                            className="edit-role"
                                                                            title={
                                                                                canEdit
                                                                                    ? "edit configuration"
                                                                                    : "No permisssion to edit configuration"
                                                                            }
                                                                            disabled={!canEdit}
                                                                            onClick={
                                                                                canEdit
                                                                                    ? () => handleEditConfig(config)
                                                                                    : undefined
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={editIcon}
                                                                                alt="edit"
                                                                                style={{ opacity: canEdit ? 1 : 0.5 }}
                                                                            />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p className="loading">No configuration record found</p>
                                                ))}
                                        </div>
                                    </div>
                                    {/* modal for update config */}
                                    <Modal
                                        show={showConfigUpdateModal}
                                        onHide={() => setShowConfigUpdateModal(false)}
                                    >
                                        <Modal.Header>
                                            <Modal.Title>Edit Config</Modal.Title>
                                            <button
                                                onClick={() => setShowConfigUpdateModal(false)}
                                                className="modal-close"
                                            >
                                                <img
                                                    src={crossIcon}
                                                    alt="Close"
                                                    style={{ width: "20px", height: "20px" }}
                                                />
                                            </button>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <Form>
                                                <Form.Group>
                                                    <Form.Label>Category</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={
                                                            editConfig.categoryName
                                                                ? editConfig.categoryName
                                                                    .replace(/[_-]/g, " ")
                                                                    .toLowerCase()
                                                                    .replace(/^\w/, (c) => c.toUpperCase())
                                                                : ""
                                                        }
                                                        disabled
                                                        className="disabled"
                                                        placeholder="Please enter the category">
                                                    </Form.Control>

                                                </Form.Group>
                                                <Form.Group className="mt-3">
                                                    <Form.Label>Assigned To </Form.Label>
                                                    <Form.Select
                                                        value={editConfig.assignedToUserId}
                                                        onChange={(e) =>
                                                            setEditConfig({
                                                                ...editConfig,
                                                                assignedToUserId: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Please enter the assignee"
                                                    >
                                                        {assigneeOptions.map(assigned => {
                                                            return (<option key={assigned.value} value={assigned.value}>
                                                                {assigned.label?.replace(/_/g, " ")}
                                                            </option>)
                                                        })
                                                        }
                                                    </Form.Select>
                                                </Form.Group>
                                            </Form>
                                        </Modal.Body>
                                        <Modal.Footer>
                                            <Button
                                                variant="cancel-cta"
                                                onClick={() => setShowConfigUpdateModal(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="update-cta"
                                                disabled={!canEdit}
                                                onClick={canEdit ? handleUpdateConfig : undefined}
                                            >
                                                Update
                                            </Button>
                                        </Modal.Footer>
                                    </Modal>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AssignmentCategory
